import React, { useState } from "react";
import { Box, Grid, Paper, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { WifiTetheringError } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import gitlabSelfManaged from "src/assets/icons/gitlab-self-hosted";
import { useAuth } from "src/components/providers/auth-context";
import notion from "../../assets/icons/notion";
import slack from "../../assets/icons/slack";
import gitlab from "../../assets/icons/gitlab";
import jira from "../../assets/icons/jira";
import googleWorkspace from "../../assets/icons/google-workspace";
import github from "../../assets/icons/github";
import IntegrationButton from "./integration-button";
import Terms from "../../components/terms";
import fetchStatus from "../../utils/api/fetch-status";
import microsoft from "../../assets/icons/microsoft";
import azure from "../../assets/icons/azure";

const svgs = {
    slack,
    jira,
    gitlab,
    google: googleWorkspace,
    github,
    gitlabSelfManaged,
    microsoft,
    azure,
    notion,
};

const OrganizationSection = ({ userIntegrations }) => {
    const [orgLevelIntegrations] = useState([
        "github",
        "jira",
        "notion",
        "gitlabSelfManaged",
        "microsoft",
        "azure",
        "google",
    ]);
    const [termsError, setTermsError] = useState(false);
    const [selectedIntegrationOrg, setSelectedIntegrationOrg] = useState();
    const [checkedTerms, setCheckedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const { isDemo, isUserPayingCustomer } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (
            userIntegrations.status === fetchStatus.PENDING ||
            userIntegrations.status === fetchStatus.REFETCHING
        ) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [userIntegrations.status]);

    const handleTermsChange = (event) => {
        setCheckedTerms(event.target.checked);
        setTermsError(false);
    };

    const handleIntegration = (clickedIntegration) => () => {
        if (termsError) setTermsError(false);
        const { integrations } = userIntegrations.data;
        const selected = integrations.find(
            (integration) => integration.name === clickedIntegration
        );
        setSelectedIntegrationOrg(selected);
    };

    React.useEffect(() => {
        if (userIntegrations.data && selectedIntegrationOrg) {
            handleIntegration(selectedIntegrationOrg.name)();
        }
    }, [userIntegrations.data]);

    const handleConnect = async () => {
        if (!selectedIntegrationOrg.connected && !selectedIntegrationOrg.centrallyManaged) {
            if (!checkedTerms) return setTermsError(true);
            if (selectedIntegrationOrg.name === "github")
                navigate("../integrations/permissions/github-app");
            if (selectedIntegrationOrg.name === "jira")
                navigate("../integrations/permissions/jira-org");
            if (selectedIntegrationOrg.name === "notion")
                navigate("../integrations/permissions/notion-org");
            if (selectedIntegrationOrg.name === "gitlabSelfManaged")
                navigate("../integrations/permissions/gitlab-self-hosted-org");
            if (selectedIntegrationOrg.name === "microsoft")
                navigate("../integrations/permissions/microsoft-org");
            if (selectedIntegrationOrg.name === "azure")
                navigate("../integrations/permissions/azure-org");
            if (selectedIntegrationOrg.name === "google")
                navigate("../integrations/permissions/google-org");
        }
        if (
            selectedIntegrationOrg.connected ||
            (!selectedIntegrationOrg.connected && selectedIntegrationOrg.centrallyManaged)
        ) {
            if (selectedIntegrationOrg.name === "github")
                navigate("../integrations/settings/github-app", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "jira")
                navigate("../integrations/settings/jira-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "notion")
                navigate("../integrations/settings/notion-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "gitlabSelfManaged")
                navigate("../integrations/settings/gitlab-self-hosted-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "microsoft")
                navigate("../integrations/settings/microsoft-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "azure")
                navigate("../integrations/settings/azure-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "google")
                navigate("../integrations/settings/google-org", {
                    replace: true,
                });
        }
        return undefined;
    };

    const renderOrgSkeletons = () => {
        return [0, 1, 2, 3].map((i) => (
            <Grid item key={i}>
                <Skeleton>
                    <IntegrationButton />
                </Skeleton>
            </Grid>
        ));
    };

    const renderError = () => {
        return [0].map((i) => (
            <Grid container item key={i} alignItems="center" direction="column">
                <Grid item>
                    <WifiTetheringError fontSize="large" />
                </Grid>
                <Grid item>Error retrieving data</Grid>
            </Grid>
        ));
    };

    const isDisabled = ({ name, connected, integrationType, centrallyManaged }) => {
        if (name === "github") return connected && integrationType !== "app" && !centrallyManaged;
        if (name === "jira") return connected && !centrallyManaged;
        if (name === "notion") return connected && !centrallyManaged;
        if (name === "gitlabSelfManaged") return connected && !centrallyManaged;
        if (name === "microsoft") return connected && !centrallyManaged;
        if (name === "azure") return connected && !centrallyManaged;
        if (name === "google") return connected && !centrallyManaged;
        return false;
    };

    const renderOrganizationIntegrations = () => {
        if (userIntegrations.status === fetchStatus.PENDING) return renderOrgSkeletons();
        if (userIntegrations.status === fetchStatus.ERROR) return renderError();
        const { integrations } = userIntegrations.data;
        const current = integrations
            .filter(({ name }) => orgLevelIntegrations.includes(name))
            .map(
                ({
                    name,
                    connected,
                    centrallyManaged,
                    invalid,
                    invalidationReason,
                    pendingInstallations,
                    integrationType,
                }) => (
                    <Grid item>
                        <IntegrationButton
                            name={name}
                            svg={svgs[name]}
                            checked={selectedIntegrationOrg && selectedIntegrationOrg.name === name}
                            connected={connected}
                            centrallyManaged={centrallyManaged}
                            invalid={invalid}
                            pendingInstallations={pendingInstallations}
                            invalidationReason={invalidationReason}
                            integrationType={integrationType}
                            action={handleIntegration(name)}
                            disabled={isDisabled({
                                name,
                                connected,
                                integrationType,
                                centrallyManaged,
                            })}
                        />
                    </Grid>
                )
            );
        return current;
    };

    const isConnected =
        (selectedIntegrationOrg &&
            selectedIntegrationOrg.connected &&
            !selectedIntegrationOrg.centrallyManaged) ||
        (selectedIntegrationOrg &&
            selectedIntegrationOrg.connected &&
            selectedIntegrationOrg.centrallyManaged) ||
        (selectedIntegrationOrg &&
            !selectedIntegrationOrg.connected &&
            selectedIntegrationOrg.centrallyManaged);

    return (
        <Paper
            style={{
                width: "100%",
                maxWidth: "794px",
                borderRadius: "14px",
                marginBottom: "32px",
            }}
            className="tour-integrations"
        >
            <Box my={3} mx={4} pb={1}>
                <Grid container spacing={5} direction="column">
                    <Grid item>
                        <h3 style={{ marginTop: 0 }}>Company-wide integrations</h3>
                    </Grid>
                    <Grid
                        container
                        item
                        spacing={2}
                        pt={0}
                        style={{ paddingTop: 0 }}
                        justifyContent="center"
                    >
                        {renderOrganizationIntegrations()}
                    </Grid>
                    {selectedIntegrationOrg &&
                        !selectedIntegrationOrg.connected &&
                        !selectedIntegrationOrg.centrallyManaged && (
                            <Grid item alignItems="center" style={{ display: "flex" }}>
                                <Terms
                                    checkedTerms={checkedTerms}
                                    handleTermsChange={handleTermsChange}
                                    termsError={termsError}
                                />
                            </Grid>
                        )}
                    {selectedIntegrationOrg && !isDemo && isUserPayingCustomer && (
                        <Grid item>
                            <LoadingButton
                                variant="contained"
                                color={isConnected ? "secondary" : "primary"}
                                onClick={handleConnect}
                                loading={loading}
                                disabled={
                                    !selectedIntegrationOrg ||
                                    (!isConnected && !isUserPayingCustomer)
                                }
                            >
                                {isConnected ? "Settings" : "Connect"}
                            </LoadingButton>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Paper>
    );
};

export default OrganizationSection;
